import { Button, Input, Textarea } from '@mui/joy';

import { FC, useState } from 'react';

import useToast from '@/components/Toast/useToast';
import { capitalize } from '@/utils/stringUtils';

import {
  ECreateActionModalState,
  TCanvasQuickAction,
  useCreateCanvasQuickAction,
  useUpdateCanvasQuickAction,
} from '../hooks/useCanvasQuickActions';

type TProps = {
  onClose: () => void;
  modalState: ECreateActionModalState;
  quickAction?: TCanvasQuickAction;
};

const CustomQuickActionModal: FC<TProps> = ({
  onClose,
  modalState,
  quickAction,
}) => {
  const toast = useToast();

  const [name, setName] = useState(quickAction?.name);
  const [prompt, setPrompt] = useState(quickAction?.prompt);

  const { trigger: createQuickAction, isMutating: isCreating } =
    useCreateCanvasQuickAction();
  const handleCreate = async () => {
    if (!name || !prompt) return;
    try {
      await createQuickAction({ json: { name, prompt } });
      onClose();
    } catch (e) {
      toast.createToast({
        title: 'Error creating quick action',
        description: (e as Error).message,
      });
    }
  };

  const { trigger: updateQuickAction, isMutating: isUpdating } =
    useUpdateCanvasQuickAction();
  const handleUpdate = async () => {
    if (!name || !prompt || !quickAction?.id) return;
    try {
      await updateQuickAction({
        json: { id: quickAction.id, name, prompt },
      });
      onClose();
    } catch (e) {
      toast.createToast({
        title: 'Error updating quick action',
        description: (e as Error).message,
      });
    }
  };

  const confirmDisabled =
    !name ||
    !prompt ||
    (name === quickAction?.name && prompt === quickAction?.prompt);
  const isLoading = isCreating || isUpdating;

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="text-lg font-semibold">
        {`${capitalize(modalState)} custom quick action`}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold">Name</span>
        <Input
          placeholder="Action name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          type="text"
        />
      </div>

      <div className="flex flex-grow flex-col gap-1">
        <span className="text-sm font-semibold">Prompt</span>
        <Textarea
          className="flex-grow"
          placeholder="Prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </div>

      <div className="flex w-full flex-row justify-center gap-2">
        <Button
          variant="outlined"
          color="neutral"
          onClick={onClose}
          className="w-full"
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          className="w-full"
          disabled={confirmDisabled}
          loading={isLoading}
          onClick={
            modalState === ECreateActionModalState.CREATE
              ? handleCreate
              : handleUpdate
          }
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};

export default CustomQuickActionModal;
